import { useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { useCreditFacilityLoanListSuspense } from "gen/endpoints/credit-facility-loan/credit-facility-loan";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { useTranslation } from "react-i18next";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import invariant from "shared/utils/TinyInvariant";

export const CreditFacilityExpensesDetailsPage: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const { creditFacilityId, fundFamilyId, fundFamilyEventId, loanId } =
    useRequiredStringParams();

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data: loans } = useCreditFacilityLoanListSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const currentLoan = loans.find((loan) => loan.id === loanId);
  invariant(currentLoan);

  const { data: adjustmentsData } =
    useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEventId,
      loanId,
      {
        read_only: true,
      },
    );

  const expensePath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId })
    .event({ fundFamilyEventId })
    .expenses({})
    .post({}).$;

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={`${t("creditFacility:postExpense.expenseDetails")}: ${
          currentLoan.name
        }`}
        documentTitle={
          fundFamilyEvent.state == FundFamilyEventStateMachineFieldsEnum.draft
            ? t("creditFacility:postExpense.workflow")
            : t("creditFacility:postExpense.dailyBreakdown")
        }
        backButton={{
          title: t("common:back"),
          path: expensePath,
        }}
      />
      <PageBody>
        <TableBox isFullHeight={true} isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {`${formatIsoDateToDisplay(
                adjustmentsData.period_start,
              )} - ${formatIsoDateToDisplay(adjustmentsData.period_end)}: ${t(
                "creditFacility:postExpense.dailyInterestExpense",
              )}`}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={adjustmentsData.table} />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
